'use client';

import { createClient } from '@/lib/supabase/client';
import { emailService } from '@/lib/services/emailService';
import { notificationService } from '@/lib/services/notificationService';

function isSchemaError(error: any): boolean {
  if (!error) return false;
  if (error.code && typeof error.code === 'string') {
    const errorClass = error.code.substring(0, 2);
    if (errorClass === '42') return true;
    if (errorClass === '23') return false;
    if (errorClass === '08') return true;
  }
  if (error.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /type.*does not exist/i,
    ];
    return schemaErrorPatterns.some((p) => p.test(error.message));
  }
  return false;
}

// ─── Visitors ─────────────────────────────────────────────────────────────────

export const visitorService = {
  async getAll() {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('visitors')
        .select('*')
        .order('rounds_played', { ascending: false });
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return data || [];
    } catch (err: any) {
      if (isSchemaError(err)) throw err;
      return [];
    }
  },

  async upsertByEmail(visitorData: {
    firstName: string;
    surname: string;
    email: string;
    mobile: string;
    dateOfBirth?: string;
    idNumber?: string;
    address?: string;
    emergencyContact?: string;
    emergencyPhone?: string;
    homeClub?: string;
    handicap?: number;
    cdhNumber?: string;
    category?: string;
    marketingOptIn?: boolean;
  }) {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('visitors')
        .upsert(
          {
            first_name: visitorData.firstName,
            surname: visitorData.surname,
            email: visitorData.email,
            mobile: visitorData.mobile,
            date_of_birth: visitorData.dateOfBirth || null,
            id_number: visitorData.idNumber || '',
            address: visitorData.address || '',
            emergency_contact: visitorData.emergencyContact || '',
            emergency_phone: visitorData.emergencyPhone || '',
            home_club: visitorData.homeClub || '',
            handicap: visitorData.handicap || 0,
            cdh_number: visitorData.cdhNumber || '',
            category: visitorData.category || 'Adult',
            marketing_opt_in: visitorData.marketingOptIn || false,
          },
          { onConflict: 'email' }
        )
        .select()
        .single();
      if (error) {
        if (isSchemaError(error)) throw error;
        return null;
      }
      return data;
    } catch (err: any) {
      if (isSchemaError(err)) throw err;
      return null;
    }
  },

  async incrementRounds(visitorId: string) {
    const supabase = createClient();
    try {
      const { data: visitor } = await supabase
        .from('visitors')
        .select('rounds_played, loyalty_points, email, first_name, surname')
        .eq('id', visitorId)
        .single();
      if (!visitor) return;
      const newRounds = (visitor.rounds_played || 0) + 1;
      const newPoints = (visitor.loyalty_points || 0) + 10;
      const isCandidate = newRounds >= 10;
      await supabase
        .from('visitors')
        .update({
          rounds_played: newRounds,
          loyalty_points: newPoints,
          last_visit: new Date().toISOString().split('T')[0],
          membership_candidate: isCandidate,
        })
        .eq('id', visitorId);

      // Trigger 2: Send loyalty reward email every 10 rounds
      if (newRounds % 10 === 0 && visitor.email) {
        const rewardMap: Record<number, string> = {
          10: 'Free round of golf (18 holes)',
          20: 'Free round of golf + complimentary cart',
          30: 'Weekend round for two players',
          40: 'Premium golf package – round + lunch',
        };
        const rewardDescription = rewardMap[newRounds] || `Special reward for ${newRounds} rounds played`;
        await emailService.sendLoyaltyReward({
          to: visitor.email,
          visitorName: `${visitor.first_name} ${visitor.surname}`,
          roundsPlayed: newRounds,
          loyaltyPoints: newPoints,
          rewardDescription,
        });
        // In-app notification alongside loyalty reward email
        await notificationService.create({
          visitorEmail: visitor.email,
          type: 'loyalty_reward',
          title: `Loyalty Reward Unlocked! 🏆`,
          message: `Amazing! You've completed ${newRounds} rounds at Maritzburg Golf Club and earned ${newPoints} points. Your reward: ${rewardDescription}. Check your email for details.`,
          metadata: { rounds_played: newRounds, loyalty_points: newPoints, reward: rewardDescription },
        });
      }
    } catch (err: any) {
      if (isSchemaError(err)) throw err;
    }
  },
};

// ─── Bookings ─────────────────────────────────────────────────────────────────

export const bookingService = {
  async getAll(date?: string) {
    const supabase = createClient();
    try {
      let query = supabase
        .from('bookings')
        .select('*')
        .order('booking_date', { ascending: false })
        .order('booking_time', { ascending: true });
      if (date) query = query.eq('booking_date', date);
      const { data, error } = await query;
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return (data || []).map((b: any) => ({
        id: b.id,
        date: b.booking_date,
        time: b.booking_time,
        visitorName: b.visitor_name,
        visitorEmail: b.visitor_email,
        players: b.players,
        holes: b.holes,
        cart: b.cart,
        greenFees: b.green_fees,
        cartFees: b.cart_fees,
        total: b.total,
        status: b.status,
        paymentMethod: b.payment_method,
        visitorId: b.visitor_id,
      }));
    } catch (err: any) {
      if (isSchemaError(err)) throw err;
      return [];
    }
  },

  async create(booking: {
    visitorId?: string;
    visitorName: string;
    visitorEmail: string;
    date: string;
    time: string;
    players: number;
    holes: number;
    cart: string;
    playerNames: string[];
    greenFees: number;
    cartFees: number;
    total: number;
    paymentMethod: string;
    consentSigned: boolean;
    marketingOptIn: boolean;
  }) {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('bookings')
        .insert({
          visitor_id: booking.visitorId || null,
          visitor_name: booking.visitorName,
          visitor_email: booking.visitorEmail,
          booking_date: booking.date,
          booking_time: booking.time,
          players: booking.players,
          holes: booking.holes,
          cart: booking.cart,
          player_names: booking.playerNames,
          green_fees: booking.greenFees,
          cart_fees: booking.cartFees,
          total: booking.total,
          status: 'confirmed',
          payment_method: booking.paymentMethod,
          consent_signed: booking.consentSigned,
          marketing_opt_in: booking.marketingOptIn,
        })
        .select()
        .single();
      if (error) {
        if (isSchemaError(error)) throw error;
        return null;
      }
      return data;
    } catch (err: any) {
      if (isSchemaError(err)) throw err;
      return null;
    }
  },

  async updateStatus(bookingId: string, status: string) {
    const supabase = createClient();
    try {
      const { error } = await supabase
        .from('bookings')
        .update({ status })
        .eq('id', bookingId);
      if (error) {
        if (isSchemaError(error)) throw error;
      }
    } catch (err: any) {
      if (isSchemaError(err)) throw err;
    }
  },

  async updateDetails(bookingId: string, updates: {
    bookingDate?: string;
    bookingTime?: string;
    holes?: number;
    cart?: string;
    players?: number;
    status?: string;
    refundStatus?: string | null;
  }) {
    const supabase = createClient();
    try {
      const payload: Record<string, any> = { updated_at: new Date().toISOString() };
      if (updates.bookingDate !== undefined) payload.booking_date = updates.bookingDate;
      if (updates.bookingTime !== undefined) payload.booking_time = updates.bookingTime;
      if (updates.holes !== undefined) payload.holes = updates.holes;
      if (updates.cart !== undefined) payload.cart = updates.cart;
      if (updates.players !== undefined) payload.players = updates.players;
      if (updates.status !== undefined) payload.status = updates.status;
      if (updates.refundStatus !== undefined) payload.refund_status = updates.refundStatus;

      const { data, error } = await supabase
        .from('bookings')
        .update(payload)
        .eq('id', bookingId)
        .select()
        .single();
      if (error) {
        if (isSchemaError(error)) throw error;
        return null;
      }
      return data;
    } catch (err: any) {
      if (isSchemaError(err)) throw err;
      return null;
    }
  },
};

// ─── Tee Slots ────────────────────────────────────────────────────────────────

export const teeSlotService = {
  async getByDate(date: string) {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('tee_slots')
        .select('*')
        .eq('slot_date', date)
        .order('slot_time', { ascending: true });
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return (data || []).map((s: any) => ({
        id: s.id,
        time: s.slot_time,
        status: s.status,
        availableSpots: s.available_spots,
        totalSpots: s.total_spots,
        bookedBy: s.booked_by,
        bookingId: s.booking_id,
      }));
    } catch (err: any) {
      if (isSchemaError(err)) throw err;
      return [];
    }
  },

  async updateSlotStatus(slotDate: string, slotTime: string, status: string, bookingId?: string) {
    const supabase = createClient();
    try {
      const { error } = await supabase
        .from('tee_slots')
        .upsert(
          {
            slot_date: slotDate,
            slot_time: slotTime,
            status,
            available_spots: status === 'booked' ? 0 : 4,
            booking_id: bookingId || null,
          },
          { onConflict: 'slot_date,slot_time' }
        );
      if (error) {
        if (isSchemaError(error)) throw error;
      }
    } catch (err: any) {
      if (isSchemaError(err)) throw err;
    }
  },
};

// ─── Transactions ─────────────────────────────────────────────────────────────

export const transactionService = {
  async getMonthlyRevenue() {
    const supabase = createClient();
    try {
      const sixMonthsAgo = new Date();
      sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
      const { data, error } = await supabase
        .from('transactions')
        .select('transaction_date, green_fees, cart_revenue, total')
        .gte('transaction_date', sixMonthsAgo.toISOString().split('T')[0])
        .order('transaction_date', { ascending: true });
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      // Group by month
      const monthMap: Record<string, { greenFees: number; cartRevenue: number; total: number; rounds: number }> = {};
      (data || []).forEach((t: any) => {
        const d = new Date(t.transaction_date + 'T12:00:00');
        const key = d.toLocaleDateString('en-ZA', { month: 'short', year: '2-digit' });
        if (!monthMap[key]) monthMap[key] = { greenFees: 0, cartRevenue: 0, total: 0, rounds: 0 };
        monthMap[key].greenFees += Number(t.green_fees) || 0;
        monthMap[key].cartRevenue += Number(t.cart_revenue) || 0;
        monthMap[key].total += Number(t.total) || 0;
        monthMap[key].rounds += 1;
      });
      return Object.entries(monthMap).map(([month, vals]) => ({ month, ...vals }));
    } catch (err: any) {
      if (isSchemaError(err)) throw err;
      return [];
    }
  },

  async create(tx: {
    bookingId?: string;
    visitorId?: string;
    visitorName: string;
    greenFees: number;
    cartRevenue: number;
    total: number;
    paymentMethod: string;
    holes: number;
  }) {
    const supabase = createClient();
    try {
      const { error } = await supabase.from('transactions').insert({
        booking_id: tx.bookingId || null,
        visitor_id: tx.visitorId || null,
        visitor_name: tx.visitorName,
        transaction_date: new Date().toISOString().split('T')[0],
        green_fees: tx.greenFees,
        cart_revenue: tx.cartRevenue,
        total: tx.total,
        payment_method: tx.paymentMethod,
        holes: tx.holes,
      });
      if (error) {
        if (isSchemaError(error)) throw error;
      }
    } catch (err: any) {
      if (isSchemaError(err)) throw err;
    }
  },
};

// ─── Loyalty Promotions ───────────────────────────────────────────────────────

export const loyaltyService = {
  async getPromotions() {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('loyalty_promotions')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: true });
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return (data || []).map((p: any) => ({
        id: p.id,
        name: p.name,
        description: p.description,
        targetRounds: p.target_rounds,
        reward: p.reward,
        category: p.category,
        activeMembers: 0,
        completions: 0,
      }));
    } catch (err: any) {
      if (isSchemaError(err)) throw err;
      return [];
    }
  },
};

// ─── Calendar Events ──────────────────────────────────────────────────────────

export const calendarService = {
  async getAll() {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('calendar_events')
        .select('*')
        .order('event_date', { ascending: true });
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return (data || []).map((e: any) => ({
        id: e.id,
        date: e.event_date,
        title: e.title,
        category: e.category,
        description: e.description,
        affectsBooking: e.affects_booking,
      }));
    } catch (err: any) {
      if (isSchemaError(err)) throw err;
      return [];
    }
  },

  async create(event: {
    date: string;
    title: string;
    category: string;
    description?: string;
    affectsBooking: boolean;
  }) {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('calendar_events')
        .insert({
          event_date: event.date,
          title: event.title,
          category: event.category,
          description: event.description || '',
          affects_booking: event.affectsBooking,
        })
        .select()
        .single();
      if (error) {
        if (isSchemaError(error)) throw error;
        return null;
      }
      return data;
    } catch (err: any) {
      if (isSchemaError(err)) throw err;
      return null;
    }
  },

  async delete(id: string) {
    const supabase = createClient();
    try {
      const { error } = await supabase.from('calendar_events').delete().eq('id', id);
      if (error) {
        if (isSchemaError(error)) throw error;
      }
    } catch (err: any) {
      if (isSchemaError(err)) throw err;
    }
  },
};

export { emailService };